import { useQuery } from "@apollo/client/react";
import { Flex, FlexItem, Loading, P, Popover } from "orcs-design-system";
import PropTypes from "prop-types";
import { useCallback, useState } from "react";
import { useGroupTypes } from "src/contexts/global/WorkspaceContext";
import { getGroupBaseQuery } from "src/queries/group.graphql";
import GroupTypeBadge from "src/components/Badge/GroupTypeBadge";
import PersistedMoveIndicator from "./PersistedMoveIndicator";
import PreviousLocationBreadcrumb from "./PreviousLocationBreadcrumb";
import SourceOfTruthMissing from "./SourceOfTruthMissing";
import {
  getSourceParentId,
  getSourceType,
  isSourceTypeMissing,
} from "./persistedMoveSource";

const ACCESSIBLE_LABEL =
  "This team was moved in TeamForm; placement is saved and may differ from the source hierarchy.";

const PersistentMoveExplanation = () => (
  <P>
    This is a persistent move, meaning that this team and its child teams have
    been moved within TeamForm and may not match source system hierarchy. This
    team will continue to live in this location until it is manually moved
    again.
  </P>
);

const PersistedMoveLoadingTooltipBody = () => (
  <Flex alignItems="center" gap="s">
    <Loading ariaLabel="Loading source location" />
    <P mb="0">Loading source location…</P>
  </Flex>
);

const PreviousGroupTypeBadge = ({ group, groupTypeOverride, groupTypes }) => {
  const sourceType = getSourceType(groupTypeOverride);

  if (!sourceType || group.type === sourceType) {
    return null;
  }

  return (
    <Flex flexDirection="column" gap="s">
      <P mb="0">This team type was changed from:</P>
      <GroupTypeBadge group={{ type: sourceType }} groupTypes={groupTypes} />
    </Flex>
  );
};

PreviousGroupTypeBadge.propTypes = {
  group: PropTypes.object.isRequired,
  groupTypeOverride: PropTypes.object,
  groupTypes: PropTypes.object,
};

const PersistedMoveSourceMissingTooltipBody = ({
  group,
  groupTypeOverride,
  groupTypes,
}) => (
  <Flex flexDirection="column" gap="s">
    <SourceOfTruthMissing />
    <PreviousGroupTypeBadge
      group={group}
      groupTypeOverride={groupTypeOverride}
      groupTypes={groupTypes}
    />
    <PersistentMoveExplanation />
  </Flex>
);

PersistedMoveSourceMissingTooltipBody.propTypes = {
  group: PropTypes.object.isRequired,
  groupTypeOverride: PropTypes.object,
  groupTypes: PropTypes.object,
};

const PersistedMoveTooltipBody = ({
  group,
  parentGroup,
  groupTypeOverride,
  groupTypes,
  sourceTypeMissing,
}) => (
  <Flex flexDirection="column" gap="s">
    <P mb="0">This team has been moved from:</P>
    <PreviousLocationBreadcrumb group={parentGroup} />
    <PreviousGroupTypeBadge
      group={group}
      groupTypeOverride={groupTypeOverride}
      groupTypes={groupTypes}
    />
    {sourceTypeMissing && <SourceOfTruthMissing />}
    <PersistentMoveExplanation />
  </Flex>
);

PersistedMoveTooltipBody.propTypes = {
  group: PropTypes.object.isRequired,
  parentGroup: PropTypes.object.isRequired,
  groupTypeOverride: PropTypes.object,
  groupTypes: PropTypes.object,
  sourceTypeMissing: PropTypes.bool,
};

const PersistedMoveParentInfo = ({ group, testId }) => {
  const groupTypes = useGroupTypes();
  const { propertyOverrides } = group;

  const { directParent, groupType: groupTypeOverride } =
    propertyOverrides || {};
  const sourceParentId = getSourceParentId(directParent);
  const sourceTypeMissing = isSourceTypeMissing(group);

  // Lazy fetch: defer the source-parent query until the user actually
  // interacts with the indicator (hover / focus / tap). Avoids firing one
  // query per moved team when a page first renders.
  const [hasInteracted, setHasInteracted] = useState(false);
  const triggerLoad = useCallback(() => setHasInteracted(true), []);

  const { data, loading } = useQuery(getGroupBaseQuery, {
    variables: { groupId: sourceParentId },
    skip: !sourceParentId || !hasInteracted,
  });

  const parentGroup = data?.group;
  // 1. No upstream parent id — do not look up the last TeamForm parent.
  // 2. Loading — query has not been triggered yet or is in flight.
  // 3. Missing — query completed but the source parent cannot be found.
  // 4. Loaded — query completed and we have the source parent.
  const isSourceParentMissing = !sourceParentId;
  const isLoadingTooltip =
    !isSourceParentMissing && (!hasInteracted || loading);
  const isSourceParentNotFound =
    !isSourceParentMissing && !isLoadingTooltip && !parentGroup;

  let tooltip;
  if (isSourceParentMissing || isSourceParentNotFound) {
    tooltip = (
      <PersistedMoveSourceMissingTooltipBody
        group={group}
        groupTypeOverride={groupTypeOverride}
        groupTypes={groupTypes}
      />
    );
  } else if (isLoadingTooltip) {
    tooltip = <PersistedMoveLoadingTooltipBody />;
  } else {
    tooltip = (
      <PersistedMoveTooltipBody
        group={group}
        parentGroup={parentGroup}
        groupTypeOverride={groupTypeOverride}
        groupTypes={groupTypes}
        sourceTypeMissing={sourceTypeMissing}
      />
    );
  }

  return (
    <FlexItem
      flex="0 0 auto"
      alignSelf="center"
      role="button"
      tabIndex={0}
      aria-label="Persisted move information"
    >
      <Popover
        direction="bottom"
        width="320px"
        text={tooltip}
        ariaLabel={ACCESSIBLE_LABEL}
      >
        <PersistedMoveIndicator
          testId={testId}
          onMouseEnter={triggerLoad}
          onFocus={triggerLoad}
          onClick={triggerLoad}
        />
      </Popover>
    </FlexItem>
  );
};

PersistedMoveParentInfo.propTypes = {
  group: PropTypes.object.isRequired,
  testId: PropTypes.string,
};

export default PersistedMoveParentInfo;
